import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Pagination } from 'antd';
import MarketingLayout from '../MarketingLayout';
import { getPublicResourceHubFeed } from '../../../services/content';
import { ContentItem } from '../../../types/content';
import { channelKeyToRouteSlug, RESOURCE_CHANNELS } from '../../../config/resourceHub';
import { useDocumentMeta } from '../../../hooks/useDocumentMeta';
import ContentThumbnail from '../../../components/ContentThumbnail';

const ResourceHubPage: React.FC = () => {
  const [rows, setRows] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(12);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const data = await getPublicResourceHubFeed({ page, page_size: pageSize });
        setRows(data.results || []);
        setTotal(data.count || 0);
      } catch {
        setRows([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [page, pageSize]);

  useDocumentMeta({
    title: '资源中心',
    description: '安飞供应链资源中心：仓库实拍、行业知识库、行业资讯与可下载资料',
  });

  return (
    <MarketingLayout>
      <section className="mx-auto max-w-6xl px-6 py-14">
        <h1 className="text-2xl font-normal text-gray-900">资源中心</h1>
        <p className="mt-3 text-sm leading-7 text-gray-600">
          仓库实拍、行业知识、行业资讯与可下载资料，帮助你更全面地了解安飞供应链和跨境仓储物流行业。
        </p>
      </section>

      <section className="mx-auto max-w-6xl px-6 pb-10">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {RESOURCE_CHANNELS.map((c) => (
            <Link
              key={c.key}
              to={`/resources/${c.routeSlug}`}
              className="rounded-xl border border-[#d9eaff] bg-white p-5 text-center shadow-sm transition hover:shadow-md hover:border-[#2f82e8]"
            >
              <div className="text-lg font-semibold text-gray-900">{c.label}</div>
            </Link>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 pb-20">
        <h2 className="mb-4 text-lg font-semibold text-gray-900">最新内容</h2>
        {loading ? (
          <div className="py-10 text-center text-sm text-gray-500">加载中...</div>
        ) : rows.length ? (
          <>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {rows.map((item) => {
                const routeSlug = channelKeyToRouteSlug(item.channel_key);
                return (
                  <Link
                    key={item.id}
                    to={`/resources/${routeSlug}/${item.slug}`}
                    className="group flex flex-col overflow-hidden rounded-xl border border-[#d9eaff] bg-white shadow-sm transition hover:shadow-md"
                  >
                    <div className="aspect-[16/10] w-full overflow-hidden bg-gray-100">
                      <ContentThumbnail
                        imageUrl={item.image_url}
                        videoUrlYoutube={item.video_url_youtube}
                        contentType={item.content_type}
                        title={item.title}
                        className="h-full w-full object-cover transition duration-200 group-hover:scale-105"
                      />
                    </div>
                    <div className="flex flex-1 flex-col gap-2 p-4">
                      <span className="text-xs font-medium text-[#2f82e8]">{item.channel_name}</span>
                      <h3 className="line-clamp-2 text-base font-semibold text-gray-900 group-hover:text-[#2f82e8]">
                        {item.title}
                      </h3>
                      {item.summary ? <p className="line-clamp-2 text-sm text-gray-500">{item.summary}</p> : null}
                    </div>
                  </Link>
                );
              })}
            </div>
            <div className="mt-8 flex justify-center">
              <Pagination current={page} pageSize={pageSize} total={total} onChange={setPage} showSizeChanger={false} />
            </div>
          </>
        ) : (
          <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-gray-500 shadow-sm">
            暂无内容，敬请期待
          </div>
        )}
      </section>
    </MarketingLayout>
  );
};

export default ResourceHubPage;
